import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { transactionAPI, fieldSettingAPI } from '../services/api';
import {
    ArrowLeft,
    User,
    MapPin,
    DollarSign,
    FileText,
    Phone,
    FileCheck,
    Building,
    Calendar
} from 'lucide-react';
import { formatDate, formatCurrency, maskResidentNumber, formatDateTime, formatContractAreaSizeWithUnit } from '../utils/formatters';
import { addRecentViewed } from '../utils/recentViewed';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import 'jspdf-font';
import {
    fillPdfForm,
    createPdfPreviewTab,
    showPdfInPreviewTab,
    showPdfPreviewError,
} from '../utils/pdfFormFiller';
import { FIELD_LABEL_DEFAULTS as FL } from '../utils/fieldLabelDefaults';

// 서류 타입 라벨 변환 함수
const getDocumentTypeLabel = (documentType) => {
    const labels = {
        'death_certificate': '사망진단서',
        'burial_certificate': '매장신고서',
        'family_register': '가족관계증명서',
        'family_relation_certificate': '가족관계증명서',
        'cremation_certificate': '화장신고서',
        'privacy_consent': '개인정보동의서',
        'other': '기타'
    };
    return labels[documentType] || documentType;
};

// 파일 다운로드 함수
const downloadFile = async (fileUrl, fileName) => {
    try {
        console.log('파일 다운로드 시작:', { fileUrl, fileName });
        
        const response = await fetch(fileUrl);
        console.log('파일 응답 상태:', response.status, response.statusText);
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const blob = await response.blob();
        console.log('파일 blob 생성:', blob.size, 'bytes');
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = fileName || 'download';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        console.log('파일 다운로드 완료:', fileName);
    } catch (error) {
        console.error('파일 다운로드 오류:', error);
        alert(`파일 다운로드에 실패했습니다: ${error.message}`);
    }
};

const ContractDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const printRef = React.useRef(null);

    const { data: contract, isLoading, error } = useQuery({
        queryKey: ['contract', id],
        queryFn: () => transactionAPI.getById(id),
        select: (response) => response.data,
    });

    // 필드 설정 조회
    const { data: fieldSettings } = useQuery({
        queryKey: ['fieldSettings', user?.cemeteryId],
        queryFn: () => fieldSettingAPI.getFieldSettingsByCemetery(user?.cemeteryId),
        select: (response) => response.data.data,
        enabled: !!user?.cemeteryId
    });

    // 필드 라벨 가져오기 헬퍼 함수
    const getFieldLabel = (fieldCode, defaultLabel) => {
        if (!fieldSettings || !fieldSettings.fields) return defaultLabel;
        const field = fieldSettings.fields.find(f => f.field_code === fieldCode);
        return field?.custom_label || field?.label || defaultLabel;
    };

    // 관리비 내역 조회 (옵셔널, 필요시에만 호출)
    const { data: managementFeesData } = useQuery({
        queryKey: ['contract-management-fees', id],
        queryFn: () => transactionAPI.getManagementFees(id),
        select: (response) => {
            return response.data?.data || [];
        },
        enabled: false, // 기본적으로 비활성화, 필요시에만 호출
    });

    React.useEffect(() => {
        if (contract?.mausoleum?.member_number) {
            const deceasedName = Array.isArray(contract.deads) && contract.deads.length > 0 ? (contract.deads[0].name || '') : '';
            addRecentViewed(contract.id, contract.mausoleum.member_number, deceasedName);
        }
    }, [contract]);

    const handleDownloadPdf = async () => {
        let previewWindow = null;

        try {
            if (!contract) {
                alert('계약 정보를 불러올 수 없습니다.');
                return;
            }

            previewWindow = createPdfPreviewTab();
            if (!previewWindow) {
                alert('팝업이 차단되어 PDF를 열 수 없습니다. 브라우저에서 이 사이트의 팝업을 허용해 주세요.');
                return;
            }

            // 관리비 내역 조회 (없으면 API 호출)
            let managementFees = managementFeesData || [];
            
            if (!managementFees || managementFees.length === 0) {
                try {
                    const response = await transactionAPI.getManagementFees(id);
                    managementFees = response.data?.data || [];
                } catch (error) {
                    console.warn('관리비 내역 조회 실패:', error);
                    // 관리비 내역이 없어도 계속 진행 (빈 배열로 처리)
                    managementFees = [];
                }
            }

            // 관리비가 15개를 초과하면 경고
            if (managementFees.length > 15) {
                const proceed = window.confirm(
                    `관리비 내역이 ${managementFees.length}개입니다. 처음 15개만 PDF에 포함됩니다. 계속하시겠습니까?`
                );
                if (!proceed) {
                    previewWindow.close();
                    return;
                }
            }

            // PDF 폼 채우기
            const pdfBytes = await fillPdfForm(contract, managementFees);
            
            // 파일명 생성
            const memberNumber = contract?.mausoleum?.member_number || id;
            const fileName = `묘적부_${memberNumber}_${new Date().toISOString().split('T')[0]}.pdf`;
                    
            showPdfInPreviewTab(previewWindow, pdfBytes, fileName);
            
        } catch (err) {
            console.error('PDF 생성 실패:', err);
            
            // 한글 인코딩 오류인 경우 특별한 메시지 표시
            const errorMessage = err.message || String(err);
            if (errorMessage.includes('WinAnsi') || 
                errorMessage.includes('encode') ||
                errorMessage.includes('Encoding') ||
                errorMessage.includes('한글 인코딩')) {
                const encodingMessage =
                    'PDF 생성 중 한글 인코딩 오류가 발생했습니다.\n\n' +
                    '원인: pdf-lib는 텍스트 필드의 appearance를 업데이트할 때 WinAnsi 인코딩을 사용하므로, ' +
                    '한글 문자를 인코딩할 수 없습니다.\n\n' +
                    '해결 방법:\n' +
                    '1. 한글 폰트 파일(예: NanumGothic.ttf)을 public/fonts/ 폴더에 추가하세요.\n' +
                    '2. 또는 생성된 PDF를 PDF 뷰어에서 열어 텍스트 필드의 값을 확인하세요.';
                if (previewWindow && !previewWindow.closed) {
                    showPdfPreviewError(previewWindow, encodingMessage);
                } else {
                    alert(encodingMessage);
                }
            } else if (previewWindow && !previewWindow.closed) {
                showPdfPreviewError(previewWindow, err.message);
            } else {
                alert(`PDF 생성 중 오류가 발생했습니다: ${err.message}`);
            }
        }
    };

    if (isLoading) {
        return <div className="loading">계약 정보를 불러오는 중...</div>;
    }

    if (error) {
        return (
            <div className="error">
                계약 정보를 불러오는 중 오류가 발생했습니다: {error.message}
            </div>
        );
    }

    if (!contract) {
        return (
            <div className="error">
                계약 정보를 찾을 수 없습니다.
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* 헤더 */}
            <div className="flex-between mb-6">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/contracts')}
                        className="btn btn-secondary"
                        style={{ padding: '8px' }}
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">계약 상세 정보</h1>
                        {/* <p className="text-gray-600">계약번호: {contract.id}</p> */}
                    </div>
                </div>

                <div className="flex gap-2">
                    <button 
                        onClick={() => navigate(`/contracts/${contract.id}/histories`)}
                        className="btn btn-outline"
                    >
                        수정 이력
                    </button>
                    <button 
                        onClick={handleDownloadPdf}
                        className="btn btn-outline"
                    >
                        PDF 다운로드
                    </button>
                    <button 
                        onClick={() => navigate(`/contracts/${contract.id}/grave-record`)}
                        className="btn btn-outline"
                    >
                        묘적부
                    </button>
                    {hasPermission(user?.role, 'CONTRACT_EDIT') && (
                        <button 
                            onClick={() => navigate(`/contracts/edit/${contract.id}`)}
                            className="btn btn-primary"
                        >
                            계약 수정
                        </button>
                    )}
                </div>
            </div>

            <div ref={printRef} className="space-y-6">
                {/* 기본 정보 */}
                <div className="card">
                    <div className="flex items-center gap-2 mb-4">
                        {/* <FileText size={20} className="text-blue-600" /> */}
                        <h2 className="text-lg font-semibold text-gray-900">계약 기본 정보</h2>
                    </div>

                    <div className="grid grid-cols-7 gap-3">
                        <div>
                            <label className="text-sm font-medium text-gray-500 mb-1">{getFieldLabel('contract_date', FL.contract_date)}</label>
                            <p className="text-gray-900">{formatDate(contract.contract_date)}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500 mb-1">계약 구분</label>
                            <p className="text-gray-900">
                                <span className={`status-badge ${contract.type === '신규' ? 'status-progress' :
                                        contract.type === '이장' ? 'status-waiting' :
                                            'status-completed'
                                    }`}>
                                    {contract.type}
                                </span>
                            </p>
                        </div>
                    </div>

                    {contract.memo && (
                        <div className="mt-4">
                            <label className="text-sm font-medium text-gray-500 mb-1">{getFieldLabel('memo', FL.memo)}</label>
                            <p className="text-gray-900 p-3 bg-gray-50 rounded-lg">
                                {contract.memo}
                            </p>
                        </div>
                    )}
                </div>

                {/* 사망자 정보 */}
                <div className="card">
                    <div className="flex items-center gap-2 mb-4">
                        {/* <User size={20} className="text-green-600" /> */}
                        <h2 className="text-lg font-semibold text-gray-900">사망자 정보</h2>
                    </div>

                    {contract.deads && contract.deads.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="min-w-full">
                                <thead>
                                    <tr className="text-left text-gray-600">
                                        <th className="py-2 pr-4">{getFieldLabel('dead_name', FL.dead_name)}</th>
                                        <th className="py-2 pr-4">{getFieldLabel('gender', FL.gender)}</th>
                                        <th className="py-2 pr-4">{getFieldLabel('burial_method_1', FL.burial_method_1)}</th>
                                        <th className="py-2 pr-4">{getFieldLabel('burial_method_2', FL.burial_method_2)}</th>
                                        <th className="py-2 pr-4">{getFieldLabel('dead_resident_number', FL.dead_resident_number)}</th>
                                        <th className="py-2 pr-4">{getFieldLabel('death_date', FL.death_date)}</th>
                                        <th className="py-2 pr-4">{getFieldLabel('death_place', FL.death_place)}</th>
                                        <th className="py-2 pr-4">{getFieldLabel('death_cause', FL.death_cause)}</th>
                                        <th className="py-2 pr-4">{getFieldLabel('burial_date', FL.burial_date)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contract.deads.map((dead) => (
                                        <tr key={dead.id} className="border-t">
                                            <td className="py-2 pr-4 font-medium">{dead.name}</td>
                                            <td className="py-2 pr-4">{dead.gender || '-'}</td>
                                            <td className="py-2 pr-4">{dead.burial_method_1 || dead.burial_method || '-'}</td>
                                            <td className="py-2 pr-4">{dead.burial_method_2 || '-'}</td>
                                            <td className="py-2 pr-4">{dead.resident_number ? maskResidentNumber(dead.resident_number) : '-'}</td>
                                            <td className="py-2 pr-4">{formatDate(dead.death_date) || '-'}</td>
                                            <td className="py-2 pr-4">{dead.death_place || '-'}</td>
                                            <td className="py-2 pr-4">{dead.death_cause || '-'}</td>
                                            <td className="py-2 pr-4">{formatDate(dead.report_date) || formatDate(dead.burial_date) || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-gray-500">사망자 정보가 등록되지 않았습니다.</p>
                    )}
                </div>

                {/* 묘소 정보 */}
                <div className="card">
                    <div className="flex items-center gap-2 mb-4">
                        {/* <MapPin size={20} className="text-purple-600" /> */}
                        <h2 className="text-lg font-semibold text-gray-900">묘소 정보</h2>
                    </div>

                    {contract.mausoleum ? (
                        <>
                            <div className="grid grid-cols-9 gap-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-500 mb-1">{getFieldLabel('member_number', FL.member_number)}</label>
                                    <p className="text-gray-900 font-medium">{contract.mausoleum.member_number}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-500 mb-1">{getFieldLabel('complex', FL.complex)}</label>
                                    <p className="text-gray-900">{contract.mausoleum.complex}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-500 mb-1">{getFieldLabel('area', FL.area)}</label>
                                    <p className="text-gray-900">{contract.mausoleum.area}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-500 mb-1">{getFieldLabel('row_number', FL.row_number)}</label>
                                    <p className="text-gray-900">{contract.mausoleum.row_number}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-500 mb-1">{getFieldLabel('number', FL.number)}</label>
                                    <p className="text-gray-900">{contract.mausoleum.number}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-500 mb-1">{getFieldLabel('tomb_type', FL.tomb_type)}</label>
                                    <p className="text-gray-900">{contract.mausoleum.tomb_type || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-500 mb-1">
                                        {contract.mausoleum.tomb_type === '매장묘' ? getFieldLabel                                ('tomb_shape', FL.tomb_shape) : getFieldLabel('number_of_tombs', FL.number_of_tombs)}
                                    </label>
                                    <p className="text-gray-900">{contract.mausoleum.number_of_tombs ?? '-'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-500 mb-1">{getFieldLabel('area_size', FL.area_size)}</label>
                                    <p className="text-gray-900">{formatContractAreaSizeWithUnit(contract.mausoleum.area_size)}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-500 mb-1">{getFieldLabel('period', FL.period)}</label>
                                    <p className="text-gray-900">{contract.mausoleum.period}</p>
                                </div>
                            </div>

                            {contract.mausoleum.etc && (
                                <div className="mt-4">
                                    <label className="text-sm font-medium text-gray-500 mb-1">{getFieldLabel('mausoleum_etc', FL.mausoleum_etc)}</label>
                                    <p className="text-gray-900 p-4 bg-gray-50 rounded-lg leading-relaxed">
                                        {contract.mausoleum.etc}
                                    </p>
                                </div>
                            )}
                        </>
                    ) : (
                        <p className="text-gray-500">묘소 정보가 배정되지 않았습니다.</p>
                    )}
                </div>

                {/* 연고자 정보 */}
                <div className="card">
                    <div className="flex items-center gap-2 mb-4">
                        {/* <Phone size={20} className="text-orange-600" /> */}
                        <h2 className="text-lg font-semibold text-gray-900">연고자 정보</h2>
                    </div>

                    {(() => {
                        // 모든 연고자 정보 수집 (중복 제거)
                        const acquaintancesSet = new Set();
                        const allAcquaintances = [];
                        
                        // 1. Transaction에 직접 연결된 연고자 (예정지 계약 등)
                        if (contract.acquaintances && Array.isArray(contract.acquaintances)) {
                            contract.acquaintances.forEach(acquaintance => {
                                const key = `${acquaintance.id}-${acquaintance.name}`;
                                if (!acquaintancesSet.has(key)) {
                                    acquaintancesSet.add(key);
                                    allAcquaintances.push(acquaintance);
                                }
                            });
                        }
                        
                        // 2. Dead를 통한 연고자 (기존 방식)
                        contract.deads?.forEach(dead => {
                            if (dead.acquaintance1) {
                                const key = `${dead.acquaintance1.id}-${dead.acquaintance1.name}`;
                                if (!acquaintancesSet.has(key)) {
                                    acquaintancesSet.add(key);
                                    allAcquaintances.push(dead.acquaintance1);
                                }
                            }
                            if (dead.acquaintance2) {
                                const key = `${dead.acquaintance2.id}-${dead.acquaintance2.name}`;
                                if (!acquaintancesSet.has(key)) {
                                    acquaintancesSet.add(key);
                                    allAcquaintances.push(dead.acquaintance2);
                                }
                            }
                        });

                        return allAcquaintances.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full">
                                    <thead>
                                        <tr className="text-left text-gray-600">
                                            <th className="py-2 pr-4">{getFieldLabel('acquaintance_name', FL.acquaintance_name)}</th>
                                            <th className="py-2 pr-4">{getFieldLabel('relationship', FL.relationship)}</th>
                                            <th className="py-2 pr-4">{getFieldLabel('phone', FL.phone)}</th>
                                            <th className="py-2 pr-4">{getFieldLabel('acquaintance_resident_number', FL.acquaintance_resident_number)}</th>
                                            <th className="py-2 pr-4">{getFieldLabel('acquaintance_address', FL.acquaintance_address)}</th>
                                            <th className="py-2 pr-4">{getFieldLabel('registration_date', FL.registration_date)}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allAcquaintances.map((acquaintance) => {
                                            const roadAddress = acquaintance.road_address || '';
                                            const addressDetail = acquaintance.address_detail || '';
                                            const fullAddress = [roadAddress, addressDetail].filter(Boolean).join(', ').trim() || acquaintance.address || '정보 없음';
                                            
                                            return (
                                                <tr key={acquaintance.id} className="border-t">
                                                    <td className="py-2 pr-4 font-medium">{acquaintance.name}</td>
                                                    <td className="py-2 pr-4">{acquaintance.relationship || '정보 없음'}</td>
                                                    <td className="py-2 pr-4">{acquaintance.phone || '정보 없음'}</td>
                                                    <td className="py-2 pr-4">{acquaintance.resident_number ? maskResidentNumber(acquaintance.resident_number) : '정보 없음'}</td>
                                                    <td className="py-2 pr-4">{fullAddress}</td>
                                                    <td className="py-2 pr-4">{formatDate(acquaintance.created_at || acquaintance.createdAt) || '정보 없음'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="text-gray-500">연고자 정보가 등록되지 않았습니다.</p>
                        );
                    })()}
                </div>

                {/* 서류 정보 */}
                {contract.documents && contract.documents.length > 0 && (
                    <div className="card">
                        <div className="flex items-center gap-2 mb-4">
                            {/* <FileText size={20} className="text-blue-600" /> */}
                            <h2 className="text-lg font-semibold text-gray-900">서류 정보</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {contract.documents.map((document) => (
                                <div key={document.id} className="border border-gray-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-sm font-medium text-gray-700">
                                            {getFieldLabel(document.document_type, FL[document.document_type] || getDocumentTypeLabel(document.document_type))}
                                        </h3>
                                        <span className="text-xs text-gray-500">
                                            {formatDate(document.uploaded_at)}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <FileText size={16} className="text-gray-400" />
                                        <button
                                            onClick={() => downloadFile(document.file_link, document.file_name)}
                                            className="text-sm text-gray-600 truncate hover:text-blue-600 cursor-pointer"
                                            title="클릭하여 다운로드"
                                        >
                                            {document.file_name || '파일명 없음'}
                                        </button>
                                    </div>
                                    {document.file_link && (
                                        <div className="flex gap-2 mt-2">
                                            <a
                                                href={document.file_link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm text-blue-600 hover:text-blue-800"
                                            >
                                                다운로드
                                            </a>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 각자 정보 */}
                {contract.engraving && contract.engraving.length > 0 && (
                    <div className="card">
                        <div className="flex items-center gap-2 mb-4">
                            {/* <FileText size={20} className="text-green-600" /> */}
                            <h2 className="text-lg font-semibold text-gray-900">각자 정보</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {contract.engraving.map((engraving) => (
                                <div key={engraving.id} className="border border-gray-200 rounded-lg p-4">
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <FileText size={16} className="text-gray-400" />
                                            <span className="text-sm text-gray-600">
                                                {engraving.content || '내용 없음'}
                                            </span>
                                        </div>
                                        {engraving.content_file && (
                                            <div className="flex items-center gap-2">
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => downloadFile(engraving.content_file, engraving.content_file_name)}
                                                        className="text-sm text-blue-600 hover:text-blue-800"
                                                    >
                                                        다운로드
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-4 text-sm text-gray-500">
                                            <span>비용: {formatCurrency(engraving.total_cost)}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 중요 메모 */}
                {contract.important_memos && contract.important_memos.length > 0 && (
                    <div className="card border-l-4 border-l-blue-500 bg-blue-50">
                        <div className="flex items-center gap-2 mb-4">
                            <FileText size={20} className="text-blue-600" />
                            <h2 className="text-lg font-semibold text-gray-900">중요 메모</h2>
                        </div>
                        <div className="space-y-3">
                            {contract.important_memos.map((memo) => (
                                <div key={memo.id} className="bg-white p-4 rounded-lg border border-blue-200">
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                                memo.category === '계약' ? 'bg-blue-100 text-blue-800' :
                                                memo.category === '개장' ? 'bg-green-100 text-green-800' :
                                                memo.category === '석물' ? 'bg-purple-100 text-purple-800' :
                                                memo.category === '관리비' ? 'bg-yellow-100 text-yellow-800' :
                                                memo.category === '장례' ? 'bg-red-100 text-red-800' :
                                                memo.category === '민원' ? 'bg-orange-100 text-orange-800' :
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                                {memo.category}
                                            </span>
                                        </div>
                                    </div>
                                    <p className="text-gray-900 whitespace-pre-wrap">{memo.content}</p>
                                    <div className="mt-2 pt-2 border-t border-gray-200">
                                        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                                            <div className="flex items-center gap-1">
                                                <Calendar size={12} />
                                                <span>등록일시: {formatDateTime(memo.created_at || memo.createdAt)}</span>
                                                {memo.creator?.name && (
                                                    <span className="ml-1">({memo.creator.name})</span>
                                                )}
                                            </div>
                                            {(memo.updated_at || memo.updatedAt) && (memo.updated_at || memo.updatedAt) !== (memo.created_at || memo.createdAt) && (
                                                <div className="flex items-center gap-1">
                                                    <Calendar size={12} />
                                                    <span>최종 수정: {formatDateTime(memo.updated_at || memo.updatedAt)}</span>
                                                    {memo.updater?.name && (
                                                        <span className="ml-1">({memo.updater.name})</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 상세 페이지 링크 (PDF 출력 시 숨김) */}
                <div className="card no-print">
                    <div className="flex items-center gap-2 mb-4">
                        {/* <FileCheck size={20} className="text-blue-600" /> */}
                        <h2 className="text-lg font-semibold text-gray-900">상세 정보</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <button
                            onClick={() => navigate(`/contracts/${contract.id}/costs`)}
                            className="btn btn-outline flex items-center justify-center space-x-2"
                        >
                            {/* <DollarSign size={16} /> */}
                            <span>비용 상세</span>
                        </button>
                        <button
                            onClick={() => navigate(`/contracts/${contract.id}/management-fees`)}
                            className="btn btn-outline flex items-center justify-center space-x-2"
                        >
                            <span>관리비 상세</span>
                        </button>
                        <button
                            onClick={() => navigate(`/contracts/${contract.id}/maintenance`)}
                            className="btn btn-outline flex items-center justify-center space-x-2"
                        >
                            {/* <Building size={20} className="text-blue-600" /> */}
                            <span>민원 상세</span>
                        </button>
                        <button
                            onClick={() => navigate(`/contracts/${contract.id}/memos`)}
                            className="btn btn-outline flex items-center justify-center space-x-2"
                        >
                            {/* <FileText size={16} /> */}
                            <span>메모 상세</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContractDetail;